import React from 'react';
import { PAGES } from './Permissions';

// ─── Référentiels partagés du module Support & Innovation ────────────────────

const PAGE_LABEL = Object.fromEntries(PAGES.map(p => [p.id, p.nom]));
export const pageLabel = (id) => PAGE_LABEL[id] || id;
// Libellé sans l'emoji de tête (« 🛟 Support & Innovation » → « Support & Innovation »)
export const pageName = (id) => {
  const nom = pageLabel(id) || '';
  const i = nom.indexOf(' ');
  return i > 0 && /[^\w\s]/.test(nom.slice(0, i)) ? nom.slice(i + 1) : nom;
};

// Les deux chemins. La valeur technique « support » est conservée pour les bugs
// (historique en base), le libellé utilisateur est « Bug ».
export const TYPES = {
  support: {
    key: 'support', label: 'Bug', plural: 'Bugs',
    tone: 'bug',
    color: 'var(--color-paused)', bg: 'var(--color-paused-light)',
  },
  evolution: {
    key: 'evolution', label: 'Amélioration', plural: 'Améliorations',
    tone: 'idea',
    color: 'var(--color-decision)', bg: 'var(--color-decision-light)',
  },
};
export const typeInfo = (t) => TYPES[t] || TYPES.support;

// Statuts techniques (pilotage super-admin)
export const STATUTS = [
  { value: 'nouveau',    label: 'Nouveau',    color: 'var(--color-info)' },
  { value: 'en_cours',   label: 'En cours',   color: 'var(--color-warning)' },
  { value: 'en_attente', label: 'En attente', color: 'var(--color-paused)' },
  { value: 'a_valider',  label: 'À valider',  color: 'var(--color-decision)' },
  { value: 'resolu',     label: 'Validé',     color: 'var(--color-success)' },
  { value: 'refuse',     label: 'Refusé',     color: 'var(--color-error)' },
];
export const statutInfo = (v) => STATUTS.find(s => s.value === v) || { value: v, label: v, color: 'var(--text-muted)' };

// Parcours vu par le demandeur : 4 étapes lisibles, sans jargon.
export const JOURNEY = ['Reçue', 'En cours', 'À vérifier', 'Terminée'];
export function journey(statut) {
  switch (statut) {
    case 'nouveau':    return { step: 0, label: 'Reçue',        tone: 'info' };
    case 'en_cours':
    case 'planifie':   return { step: 1, label: 'En cours',     tone: 'warning' };
    case 'en_attente': return { step: 1, label: 'En pause',     tone: 'paused' };
    case 'a_valider':  return { step: 2, label: 'À vérifier',   tone: 'decision' };
    case 'resolu':
    case 'valide':     return { step: 3, label: 'Terminée',     tone: 'success' };
    case 'refuse':     return { step: -1, label: 'Non retenue', tone: 'muted' };
    default:           return { step: 0, label: 'Reçue',        tone: 'info' };
  }
}
export const isClosed = (statut) => ['resolu', 'valide', 'refuse'].includes(statut);

export const ticketNum = (n) => (n ? `#${String(n).padStart(5, '0')}` : '');

export function formatDate(d, withYear = true) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', withYear
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' });
}

export function relativeDate(d) {
  if (!d) return '';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} jours`;
  return `le ${formatDate(d, days < 300)}`;
}

export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(ta);
  }
}

// ─── Icônes (SVG inline, trait 2px, style Lucide) ─────────────────────────────
const svg = (children, size = 20) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
export const Icon = {
  bug: (s) => svg(<>
    <path d="m8 2 1.88 1.88" /><path d="M14.12 3.88 16 2" /><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" /><path d="M12 20v-9" />
    <path d="M6.53 9C4.6 8.8 3 7.1 3 5" /><path d="M6 13H2" /><path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" /><path d="M22 13h-4" /><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
  </>, s),
  idea: (s) => svg(<>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" /><path d="M10 22h4" />
  </>, s),
  arrowRight: (s) => svg(<><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>, s),
  arrowLeft: (s) => svg(<><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></>, s),
  check: (s) => svg(<path d="M20 6 9 17l-5-5" />, s),
  monitor: (s) => svg(<><rect width="20" height="14" x="2" y="3" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /></>, s),
  info: (s) => svg(<><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>, s),
  message: (s) => svg(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />, s),
  calendar: (s) => svg(<><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></>, s),
  search: (s) => svg(<><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>, s),
  copy: (s) => svg(<><rect width="14" height="14" x="8" y="8" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></>, s),
  link: (s) => svg(<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>, s),
  wand: (s) => svg(<><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" /><path d="m14 7 3 3" /><path d="M5 6v4" /><path d="M19 14v4" /><path d="M10 2v2" /><path d="M7 8H3" /><path d="M21 16h-4" /><path d="M11 3H9" /></>, s),
  send: (s) => svg(<><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>, s),
  image: (s) => svg(<><rect width="18" height="18" x="3" y="3" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21" /></>, s),
  edit: (s) => svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>, s),
};

// ─── Suivi visuel : Reçue → En cours → À vérifier → Terminée ─────────────────
export function JourneyTrack({ statut, compact = false }) {
  const j = journey(statut);
  if (j.step === -1) {
    return <span className="sinv-status sinv-status--muted">{j.label}</span>;
  }
  if (compact) {
    return (
      <span className={`sinv-status sinv-status--${j.tone}`} title={j.label}>
        <span className="sinv-mini-track" aria-hidden="true">
          {JOURNEY.map((_, i) => <span key={i} className={i <= j.step ? 'on' : ''} />)}
        </span>
        {j.label}
      </span>
    );
  }
  return (
    <ol className={`sinv-track sinv-track--${j.tone}`} aria-label={`Avancement : ${j.label}`}>
      {JOURNEY.map((label, i) => (
        <li key={label} className={i < j.step ? 'is-done' : i === j.step ? 'is-current' : ''} aria-current={i === j.step ? 'step' : undefined}>
          <span className="sinv-track-dot">{i < j.step ? Icon.check(14) : null}</span>
          <span className="sinv-track-label">{i === j.step ? j.label : label}</span>
        </li>
      ))}
    </ol>
  );
}
